import { toast } from "react-toastify";
import api from "../../services/api";
import type {
  IApproveUserResponse,
  ICreateUserParams,
  ICreateUserResponse,
  IEditUserParams,
  IEditUserResponse,
  IForgotPasswordParams,
  IForgotPasswordResponse,
  IGetLogsAllParams,
  IGetLogsAllResponse,
  IGetLogsResponse,
  IGetUserByEmailAndHospitalParams,
  IGetUserByEmailAndHospitalResponse,
  IGetUserDoctorResponse,
  IGetUserResponse,
  IGetUsersByHospitalsUserParams,
  IGetUsersByHospitalsUserResponse,
  IGetUsersParams,
  IGetUsersResponse,
  IInactiveUserParams,
  IInactiveUserResponse,
  IUpdateUserSpecialtyParams,
  IUpdateUserSpecialtyResponse,
  IUser,
} from "./types";
import type { AxiosResponse } from "axios";

export function createUsersUtils() {
  return {
    async getUsers(params?: IGetUsersParams) {
      try {
        const response = await api.get<IGetUsersResponse>("/list/users", {
          params,
        });
        return response?.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getLogs(userId: number) {
      try {
        const response = await api.get<IGetLogsResponse>(
          `/list/logs/user/${userId}`,
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getLogsAll(params: IGetLogsAllParams) {
      try {
        const response = await api.get<IGetLogsAllResponse>(
          `/list/logs/users`,
          { params },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getUser(userId: number) {
      try {
        const response = await api.get<IGetUserResponse>(
          `/show/user/${userId}`,
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getUserDoctor(userCRM: string, userUF: string) {
      try {
        const response = await api.get<IGetUserDoctorResponse>(
          `/list/doctors?crm=${userCRM}&uf=${userUF}`,
        );

        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async approveUser(id: number, data: Partial<IUser>) {
      try {
        const response = await api.post<IApproveUserResponse>(
          `/approve/user/${id}`,
          data,
        );

        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response.data;
      } catch (error: any) {
        console.error("Erro na aprovação do usuário:", error);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        throw error; // Re-throw to let mutation handle it
      }
    },

    async getUsersByGroup(Id: any, params: any) {
      try {
        const response = await api.get(`/list/group/user/${Id}`, { params });
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getUsersByHospitalsUser(params: IGetUsersByHospitalsUserParams) {
      try {
        const data = (
          await api.get<IGetUsersByHospitalsUserResponse>(`/users/list`, {
            params,
          })
        ).data;
        return data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getUsersByHospital(Id: any, params: any) {
      try {
        const response = await api.get(`hospital/user/list/${Id}`, { params });
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async createUser(
      data: ICreateUserParams,
    ): Promise<AxiosResponse<ICreateUserResponse, any>> {
      try {
        const response = await api.post<ICreateUserResponse>(
          `/user/create`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        return error;
      }
    },

    async editUser(data: IEditUserParams, userId: number) {
      try {
        const response = await api.put<IEditUserResponse>(
          `/edit/user/${userId}`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }

        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async editDoctor(data: any, userId: any) {
      try {
        const response = await api.post(`/edit/user/doctor/${userId}`, data);
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }

        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async editUserImage(data: any) {
      try {
        const response = await api.post(`/upload/user/image`, data);
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async inactiveUser(userId: number, data: IInactiveUserParams) {
      try {
        const response = await api.put<IInactiveUserResponse>(
          `inactivate/user/${userId}`,
          data,
        );
        return response;
      } catch (error: any) {
        console.error("Erro na ativação/inativação do usuário:", error);
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
        throw error; // Re-throw to let mutation handle it
      }
    },

    async forgotPassword(data: IForgotPasswordParams) {
      try {
        const response = await api.post<IForgotPasswordResponse>(
          `password/forgot`,
          data,
        );
        if (response?.data?.message) {
          toast.success(response?.data?.message);
        }
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async updateUserSpecialty(
      userId: number,
      data: IUpdateUserSpecialtyParams,
    ) {
      try {
        const response = api.put<IUpdateUserSpecialtyResponse>(
          `user/especialidade/${userId}`,
          data,
        );
        return response;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },

    async getUserByEmail(params: IGetUserByEmailAndHospitalParams) {
      try {
        const response = await api.get<IGetUserByEmailAndHospitalResponse[]>(
          "/users",
          { params },
        );
        return response.data;
      } catch (error: any) {
        if (error?.response?.data?.message) {
          toast.error(error?.response?.data?.message);
        }
      }
    },
  };
}
